import React, { useState, useEffect, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock, GraphContainer, ResultMessage } from '../SimulationTemplate';
import { Text } from '../../../components/ui/typography/Text';
import { OutlineButton } from '../../../components/ui/buttons/OutlineButton';
import { AccentButton } from '../../../components/ui/buttons/AccentButton';
import { Input } from '../../../components/ui/Input';
import { Brain, Microscope, BookOpen, BarChart3 } from 'lucide-react';
import { Wheel } from './Wheel';
import { ResultsGraph } from './ResultsGraph';

/**
 * Anchoring Effect Simulation
 * 
 * This simulation demonstrates how initial reference points (anchors) can influence
 * our numerical estimates, even when those reference points are arbitrary.
 * Based on the classic experiment by Tversky & Kahneman (1974).
 */
const TemplatedAnchoringSimulation: React.FC = () => {
  // ========== STATE MANAGEMENT ==========
  const [started, setStarted] = useState(false);
  const [stage, setStage] = useState<'intro' | 'wheel' | 'comparison' | 'estimate' | 'results'>('intro');
  const [showEstimate, setShowEstimate] = useState(false);
  const [userEstimate, setUserEstimate] = useState<string>('');
  const [anchor, setAnchor] = useState<number | null>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [comparison, setComparison] = useState<string | null>(null);
  const [canChangeEstimate, setCanChangeEstimate] = useState(true);
  const [angularVelocity, setAngularVelocity] = useState(0);
  const [lastTimestamp, setLastTimestamp] = useState<number | null>(null);
  const simulationRef = useRef<HTMLDivElement>(null);

  // ========== DATA GENERATION ==========
  const generateDistributionData = () => {
    const data = [];
    for (let x = 0; x <= 100; x += 1) {
      const y1 = (1 / (10 * Math.sqrt(2 * Math.PI))) * 
                Math.exp(-0.5 * Math.pow((x - 25) / 10, 2)) * 0.4;
      const y2 = (1 / (10 * Math.sqrt(2 * Math.PI))) * 
                Math.exp(-0.5 * Math.pow((x - 45) / 10, 2)) * 0.4;
      data.push({ 
        x, 
        'Saw 10': y1,
        'Saw 65': y2,
        'Anchor 10': x === 10 ? 0.4 : undefined,
        'Anchor 65': x === 65 ? 0.4 : undefined,
        'Average 25%': x === 25 ? 0.4 : undefined,
        'Average 45%': x === 45 ? 0.4 : undefined
      });
    }
    return data;
  };

  const distributionData = generateDistributionData();
  
  // ========== EVENT HANDLERS ==========
  const generateAnchor = () => {
    setIsSpinning(true);
    setStage('wheel');
    
    // Randomly choose between 10 and 65
    const randomAnchor = Math.random() < 0.5 ? 10 : 65;
    setAnchor(randomAnchor);
    
    // Calculate target position
    const targetDegrees = randomAnchor === 10 ? 36 : 234;
    const finalRotation = 2160 + targetDegrees;
    
    setAngularVelocity(finalRotation / 3);
    setLastTimestamp(Date.now());
    setRotation(0);
  };

  // Wheel animation effect
  useEffect(() => {
    if (isSpinning && angularVelocity > 0 && lastTimestamp !== null && anchor !== null) {
      const animate = () => {
        const now = Date.now();
        const deltaTime = (now - lastTimestamp) / 1000;
        
        const friction = 0.95;
        const newVelocity = angularVelocity * Math.pow(friction, deltaTime * 60);
        
        setRotation(prev => prev + newVelocity * deltaTime);
        setAngularVelocity(newVelocity);
        setLastTimestamp(now);

        if (newVelocity < 0.1) {
          setIsSpinning(false);
          setStage('comparison');
          setRotation(anchor === 10 ? 220 : 22);
        } else {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [isSpinning, angularVelocity, lastTimestamp, anchor]);

  const handleEstimateChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    // Allow empty input or numbers between 0 and 100
    if (value === '' || /^\d{1,3}$/.test(value)) {
      const numValue = parseInt(value, 10);
      if (value === '' || (numValue >= 0 && numValue <= 100)) {
        // Strip leading zeros unless the value is just "0"
        const strippedValue = value === '0' ? '0' : value.replace(/^0+/, '');
        setUserEstimate(strippedValue);
      }
    }
  };

  const handleReset = () => {
    setStarted(false);
    setStage('intro');
    setShowEstimate(false);
    setUserEstimate('');
    setAnchor(null);
    setRotation(0);
    setComparison(null);
    setCanChangeEstimate(true);
    setIsSpinning(false);
    
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ========== SIMULATION CONTENT ==========
  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      {/* Wheel Section - always visible */}
      <SimulationStep 
        title="Spin the Wheel" 
        isActive={stage === 'wheel'}
      >
        <div className="flex flex-col items-center space-y-6">
          <div className="w-full max-w-md">
            <Wheel rotation={rotation} isSpinning={isSpinning} />
          </div>

          {stage === 'wheel' && !isSpinning && (
            <AccentButton
              onClick={generateAnchor}
              disabled={isSpinning || stage !== 'wheel'}
            >
              Spin the Wheel
            </AccentButton>
          )}
        </div>
      </SimulationStep>

      {/* Comparison Question */}
      {(stage === 'comparison' || stage === 'estimate' || stage === 'results') && (
        <SimulationStep 
          title="Make Your Choice" 
          isActive={stage === 'comparison'}
        >
          <div className="space-y-4">
            <Text className="text-center text-gray-700">
              The wheel stopped at <span className="text-orange-600 font-medium">{anchor}</span>. Is the percentage of African nations in the UN higher or lower than this number?
            </Text>
            <div className="flex justify-center gap-4">
              <OutlineButton 
                onClick={() => {
                  setComparison('lower');
                  setStage('estimate');
                }}
                active={comparison === 'lower'}
                disabled={stage !== 'comparison'}
              >
                Lower
              </OutlineButton>
              <OutlineButton 
                onClick={() => {
                  setComparison('higher');
                  setStage('estimate');
                }}
                active={comparison === 'higher'}
                disabled={stage !== 'comparison'}
              >
                Higher
              </OutlineButton>
            </div>
          </div>
        </SimulationStep>
      )}

      {/* Estimate Input */}
      {(stage === 'estimate' || stage === 'results') && (
        <SimulationStep 
          title="Your Estimate" 
          isActive={stage === 'estimate'}
        >
          <div className="space-y-4">
            <Text className="text-center text-gray-700">
              What percentage of UN member nations do you think are African?
            </Text>
            <div className="max-w-xs mx-auto">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={userEstimate}
                onChange={handleEstimateChange}
                disabled={!canChangeEstimate || stage === 'results'}
                placeholder="Enter a number between 0 and 100"
              />
            </div>
            {stage === 'estimate' && !showEstimate && userEstimate && (
              <div className="flex justify-center mt-6">
                <AccentButton
                  onClick={() => {
                    setShowEstimate(true);
                    setCanChangeEstimate(false);
                    setStage('results');
                  }}
                >
                  See Results
                </AccentButton>
              </div>
            )}
          </div>
        </SimulationStep>
      )}

      {/* Results */}
      {stage === 'results' && showEstimate && (
        <div className="space-y-6">
          {/* Understanding Results Block */}
          <ResultBlock
            title="Understanding the Results"
            icon={Brain}
            variant="highlight"
          >
            <div className="space-y-6">
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  Your Results
                </Text>
                <Text className="text-gray-700">
                  Your estimate of <span className="text-blue-600 font-medium">{userEstimate}%</span> falls {Number(userEstimate) < 35 ? 'below' : 'above'} the actual percentage, which was <span className="text-emerald-600 font-medium">35%</span>.
                </Text>
                
                <ResultMessage 
                  isCorrect={Number(userEstimate) >= 30 && Number(userEstimate) <= 40} 
                  message={`Your estimate was ${Number(userEstimate) >= 30 && Number(userEstimate) <= 40 ? 'quite accurate' : 'influenced by the anchor value'}. Most people who saw ${anchor} estimated ${anchor === 10 ? 'around 25%' : 'around 45%'}.`}
                />
              </div>

              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  How This Relates to Anchoring Bias
                </Text>
                <Text className="text-gray-700">
                  You saw the number <span className="text-orange-600 font-medium">{anchor}</span> on the wheel, and estimated that <span className="text-blue-600 font-medium">{userEstimate}%</span> of UN nations were African.
                </Text>
                <Text className="text-gray-700">
                  This demonstrates the anchoring effect - how initial information (even if random and irrelevant) can significantly influence our numerical judgments. Even though the wheel number was clearly random, it likely influenced your estimate.
                </Text>
              </div>

              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  What the Original Study Found
                </Text>
                <Text className="text-gray-700">
                  In 1974, Tversky and Kahneman conducted this experiment to demonstrate how arbitrary numbers can influence our judgments. They used a rigged wheel of fortune that would stop at either 10 or 65.
                </Text>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Participants who saw 10 estimated around 25% on average</li>
                  <li>Those who saw 65 estimated around 45% on average</li>
                  <li>The actual percentage was 35%</li>
                </ul>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 overflow-x-auto overflow-y-hidden">
                  <div className="w-full min-w-[400px] h-[450px]">
                    <ResultsGraph 
                      distributionData={distributionData} 
                      userEstimate={userEstimate}
                    />
                  </div>
                </div>
                <Text className="text-gray-700">
                  The graph shows how seeing different numbers on the wheel influenced people's estimates in the original study. This demonstrated how our minds can be unconsciously influenced by initial reference points, even when they're completely arbitrary.
                </Text>
              </div>
            </div>
          </ResultBlock>

          {/* About the Study Block */}
          <ResultBlock
            title="About the Study"
            icon={BookOpen}
            variant="muted"
          >
            <div className="space-y-4">
              <Text className="text-gray-700">
                Based on research by Tversky & Kahneman (1974), "Judgment under Uncertainty: Heuristics and Biases," published in Science. The study demonstrated how arbitrary numbers can serve as reference points that influence numerical estimates, even when those numbers are clearly irrelevant.
              </Text>
              <Text className="text-sm text-gray-500 italic">
                This simulation is designed for educational purposes. Individual results may vary.
              </Text>
            </div>
          </ResultBlock>

          {/* Try Again Button */}
          <div className="flex justify-center">
            <AccentButton onClick={handleReset}>
              Try Again
            </AccentButton>
          </div>
        </div>
      )}
    </div>
  );

  // ========== MAIN COMPONENT RENDER ==========
  return (
    <SimulationTemplate
      title="The Anchoring Effect"
      description="Experience how random numbers can unconsciously influence your judgments in this recreation of Tversky & Kahneman's famous experiment."
    >
      {!started ? (
        <div className="flex justify-center">
          <AccentButton onClick={() => {
            setStarted(true);
            setStage('wheel');
          }}>
            Start Simulation
          </AccentButton>
        </div>
      ) : (
        simulationContent
      )}
    </SimulationTemplate>
  );
};

export default TemplatedAnchoringSimulation; 